import React from 'react';
import { Text, Button } from '@vibe/core';
import { useSubscription } from '../../context/SubscriptionContext';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

/**
 * Blocks the app when the subscription is inactive (expired, trial ended, payment failed).
 * Shows a full-screen prompt to subscribe via monday.com marketplace.
 *
 * Fail-open: while loading or on error, children are shown.
 */
const SubscriptionGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isActive, isLoading, isExpired, isPastDue, isTrial, status } = useSubscription();

  // Fail-open while loading
  if (isLoading) return <>{children}</>;

  // Active subscription (including trial, cancelled-but-in-period, grace period) → allow
  if (isActive) return <>{children}</>;

  // Determine the message
  let title = 'Subscription Required';
  let message = 'Your WorkHub subscription is not active. Please subscribe to continue using the app.';

  if (isExpired && isTrial) {
    title = 'Trial Ended';
    message = 'Your free trial of WorkHub has ended. Subscribe to keep using all features.';
  } else if (isExpired) {
    title = 'Subscription Expired';
    message = 'Your WorkHub subscription has expired. Please renew to continue.';
  } else if (isPastDue) {
    title = 'Payment Issue';
    message = 'We were unable to process your payment. Please update your billing information to continue using WorkHub.';
  }

  const handleSubscribe = () => {
    try {
      // Opens monday.com's plan selection dialog
      monday.execute('openPlanSelection' as any);
    } catch {
      // Fallback: open marketplace page
      window.open('https://monday.com/marketplace', '_blank');
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '40px',
      textAlign: 'center',
      gap: '16px',
    }}>
      <Text type={Text.types.TEXT1} weight={Text.weights.BOLD} style={{ fontSize: '20px' }}>
        {title}
      </Text>
      <Text type={Text.types.TEXT2} style={{ maxWidth: '400px', color: '#676879' }}>
        {message}
      </Text>
      <Button onClick={handleSubscribe} style={{ marginTop: '8px' }}>
        Subscribe Now
      </Button>
      {status?.periodEnd && (
        <Text type={Text.types.TEXT2} style={{ fontSize: '12px', color: '#adadad', marginTop: '4px' }}>
          Last active period ended: {new Date(status.periodEnd).toLocaleDateString()}
        </Text>
      )}
    </div>
  );
};

export default SubscriptionGate;
